"use client"

import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { useState, useCallback, useRef } from "react";
import RecordCard from "@/components/card/record-card/record-card";
import RecordCtaCard from "@/components/card/record-cta-card/record-cta-card";
import { useApp } from "@/context/AppContext";

const splitIntoThree = (arr) => {
  const cols = [[], [], []];
  const len = arr.length;
  const isMultipleOfFive = len > 0 && len % 5 === 0;

  arr.forEach((item, index) => {
    item.__index = index;
    if (isMultipleOfFive && index === len - 1) {
      cols[1].push(item);
    } else {
      cols[index % 3].push(item);
    }
  });
  return cols;
};

export default function RecordsGalleryGroup({
  recordCategoryType = 'curated',
  cards,
  pct = 0,
  batchSize = 15,
  offsetDir = 1,
}) {

  const [batchesInView, setBatchesInView] = useState(new Set());
  const colsRef = useRef([]);
  const { windowSize } = useApp()

  const cols = splitIntoThree(cards);

  const onBatchEnter = useCallback((batchIndex) => {
    setBatchesInView(prev => {
      if (prev.has(batchIndex)) return prev;
      const next = new Set(prev);
      next.add(batchIndex);
      return next;
    });
  }, []);

  useGSAP(() => {
    // const offsetY = (cards.length / 200) * windowSize.height;
    if (colsRef.current && windowSize.width >= 768) {
      gsap.to(colsRef.current[1], {
        yPercent: -pct * 5
      })
    }
  }, [pct, windowSize, colsRef])

  const gridRef = useRef(null)

  // Intercept Tab within the grid to follow logical (left-to-right) order
  // instead of DOM (column-by-column) order
  const handleGridKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;

    const grid = gridRef.current;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll("[data-focus-index]")).sort(
      (a, b) => Number(a.dataset.focusIndex) - Number(b.dataset.focusIndex),
    );

    const active = document.activeElement?.closest("[data-focus-index]");
    const currentIdx = cards.indexOf(active);
    if (currentIdx === -1) return;

    const nextIdx = e.shiftKey ? currentIdx - 1 : currentIdx + 1;

    // At edges, let browser handle naturally (focus leaves the grid)
    if (nextIdx < 0 || nextIdx >= cards.length) return;

    e.preventDefault();
    const focusable = cards[nextIdx].querySelector(
      'a[href], button, [tabindex="0"]',
    );
    focusable?.focus();
  }, []);

  return (
    <div className="w-full gap-y-20 lg:gap-y-14 xl:gap-y-[6.625rem] grid-3" ref={gridRef} onKeyDown={handleGridKeyDown}>
      {cols.map((cards, index) => {
        const cardInertia = index === 1 ? 1 : 0.8;

        return (
          <div
            key={index}
            ref={(el) => colsRef.current[index] = el}
            className={clsx(
              "flex flex-col gap-y-20 md:gap-y-14 xl:gap-y-[6.625rem] relative will-change-transform",
              index === 1 && offsetDir === 1 && 'md:top-[4.125rem] xl:top-[6.625rem]',
              index !== 1 && offsetDir === -1 && 'md:top-[4.125rem] xl:top-[6.625rem]',
              `gallery-col-${index}`
            )}
          >
            {cards.map((card, cardIndex) => {
              const batchIndex = Math.floor(card.__index / batchSize);
              const cardIndexWithinBatch = card.__index % batchSize;
              const isFirstInBatch = cardIndexWithinBatch === 0;
              const isParentInView = batchesInView.has(batchIndex);

              if (card.isCta) {
                return (
                  <div data-focus-index={card.__index} key={card.__index}>
                    <RecordCtaCard
                      className="px-[10%]"
                      intertia={cardInertia}
                      isParentInView={isParentInView}
                      index={cardIndexWithinBatch}
                      
                      isFirstInBatch={isFirstInBatch}
                      batchIndex={batchIndex}
                      onBatchEnter={onBatchEnter}
                    />
                  </div>
                );
              }

              return (
                <div data-focus-index={card.__index} key={card.__index}>
                  <RecordCard
                    recordCategoryType={recordCategoryType}
                    intertia={cardInertia}
                    isParentInView={isParentInView}
                    data={card}
                    index={cardIndexWithinBatch}
                    isFirstInBatch={isFirstInBatch}
                    batchIndex={batchIndex}
                    onBatchEnter={onBatchEnter}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
